import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";

import type { OfferChange } from "../lib/offerChanges";
import type { PointsChange } from "../lib/pointsChanges";
import { reviewEvent } from "../lib/pointsPage";
import {
  ICON, dismissRows, offerRow, pointsRow, restoreRows, type ChangeGroup, type ChangeRow,
} from "./changeRows";

const SHOWN = 6;
const UNDO_MS = 5000;

/**
 * A dismissable card of changes, on Points, Offers and Refresh. A dismissal
 * hides the row at once and is saved in the background, which removes it from
 * every page that shows it; Undo brings it back. The card is gone once nothing
 * is left. A held reading is settled by Approve or Reject instead, after which
 * `onReviewed` lets the page fetch the feed again.
 */
export function ChangesCard({
  title,
  groups,
  onError,
  onReviewed,
}: {
  title: string;
  groups: ChangeGroup[];
  onError: (message: string) => void;
  onReviewed?: () => void;
}) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(true);
  const [all, setAll] = useState(false);
  const [undo, setUndo] = useState<{ rows: ChangeRow[]; text: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const id = useId();

  useEffect(() => () => clearTimeout(timer.current), []);

  // By the moves dismissed, not the thing: the same balance moving again after
  // a dismissal is a new change and must show.
  const tag = (r: ChangeRow) => `${r.area}:${r.keys.join(",")}`;
  const visible = groups
    .map((g) => ({ ...g, rows: g.rows.filter((r) => !hidden.has(tag(r))) }))
    .filter((g) => g.rows.length > 0);
  const live = visible.flatMap((g) => g.rows);
  const dismissable = live.filter((r) => r.review === undefined);

  const hide = (rows: ChangeRow[]) => {
    setHidden((h) => new Set([...h, ...rows.map(tag)]));
    setUndo({ rows, text: rows.length === 1 ? "Dismissed" : `Dismissed ${rows.length}` });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setUndo(null), UNDO_MS);
    dismissRows(rows).catch((e: unknown) => {
      setHidden((h) => new Set([...h].filter((k) => !rows.some((r) => tag(r) === k))));
      onError(e instanceof Error ? e.message : String(e));
    });
  };

  const review = (r: ChangeRow, state: "approved" | "rejected") => {
    if (r.review === undefined) return;
    setHidden((h) => new Set([...h, tag(r)]));
    reviewEvent(r.review, state)
      .then(() => onReviewed?.())
      .catch((e: unknown) => {
        setHidden((h) => new Set([...h].filter((k) => k !== tag(r))));
        onError(e instanceof Error ? e.message : String(e));
      });
  };

  const restore = () => {
    if (!undo) return;
    const { rows } = undo;
    setUndo(null);
    clearTimeout(timer.current);
    setHidden((h) => new Set([...h].filter((k) => !rows.some((r) => tag(r) === k))));
    restoreRows(rows).catch((e: unknown) => {
      setHidden((h) => new Set([...h, ...rows.map(tag)]));
      onError(e instanceof Error ? e.message : String(e));
    });
  };

  // "Show all" counts across groups, so a long Points half cannot push the
  // Offers half off the card.
  let budget = all ? Infinity : SHOWN;

  return (
    <>
      {live.length > 0 && (
        <section
          aria-labelledby={id}
          className="rounded-xl border border-blue-400/30 bg-blue-400/[0.07]"
        >
          <div className="flex items-center gap-2.5 py-2.5 pl-4 pr-2.5 text-sm">
            <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-blue-300" />
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              className="flex items-center gap-2.5 font-semibold"
            >
              <span id={id}>{title}</span>
              <span className="text-xs font-normal text-neutral-400">{live.length} new</span>
              <span aria-hidden className={`text-neutral-500 transition-transform ${open ? "" : "-rotate-90"}`}>
                ▾
              </span>
            </button>
            {dismissable.length > 0 && (
              <button
                type="button"
                onClick={() => hide(dismissable)}
                className="btn btn-ghost btn-sm ml-auto"
              >
                Dismiss all
              </button>
            )}
          </div>
          {open && (
            <>
              {visible.map((g) => {
                const rows = g.rows.slice(0, Math.max(budget, 0));
                budget -= rows.length;
                if (rows.length === 0) return null;
                return (
                  <div key={g.label ?? "all"} className="border-t border-blue-400/20 px-4">
                    {g.label && (
                      <div className="flex items-center gap-2 pt-2.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                        {g.label}
                        {g.link && (
                          <Link to={g.link.to} className="ml-auto text-xs font-normal normal-case tracking-normal text-blue-300">
                            {g.link.text}
                          </Link>
                        )}
                      </div>
                    )}
                    <ul>
                      {rows.map((r) => (
                        <Row
                          key={tag(r)}
                          row={r}
                          onDismiss={() => hide([r])}
                          onReview={(state) => review(r, state)}
                        />
                      ))}
                    </ul>
                  </div>
                );
              })}
              {live.length > SHOWN && (
                <div className="px-4 pb-3 text-xs">
                  <button type="button" onClick={() => setAll((a) => !a)} className="py-1 text-blue-300">
                    {all ? "Show fewer" : `Show all ${live.length}`}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}
      {undo && (
        <div
          role="status"
          className="fixed bottom-[calc(20px+env(safe-area-inset-bottom,0px))] left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-neutral-100 py-2 pl-3.5 pr-2 text-sm font-semibold text-neutral-950"
        >
          <span>{undo.text}</span>
          <button type="button" onClick={restore} className="px-1.5 py-1 font-bold text-blue-700">
            Undo
          </button>
        </div>
      )}
    </>
  );
}

function Row({
  row: r,
  onDismiss,
  onReview,
}: {
  row: ChangeRow;
  onDismiss: () => void;
  onReview: (state: "approved" | "rejected") => void;
}) {
  const icon = ICON[r.glyph];
  return (
    <li
      data-testid="change-item"
      className="grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-b border-blue-400/10 py-2.5 text-sm last:border-b-0 sm:grid-cols-[28px_minmax(0,1fr)_auto_auto]"
    >
      <div aria-hidden className={`grid h-7 w-7 place-items-center rounded-lg text-xs font-bold ${icon.tone}`}>
        {icon.glyph}
      </div>
      <div className="min-w-0">
        <div className="truncate font-semibold">
          {r.title}
          {r.sub && <span className="font-normal text-neutral-400"> · {r.sub}</span>}
        </div>
        <div className="mt-0.5 text-[12.5px] text-neutral-300">{r.body}</div>
      </div>
      <div className="col-start-2 row-start-2 text-xs text-neutral-500 sm:col-start-auto sm:row-start-auto sm:text-right">
        {r.day}
      </div>
      {r.review === undefined ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`Dismiss ${r.title} change`}
          className="h-8 w-8 rounded-lg text-lg leading-none text-neutral-500 hover:bg-white/5 hover:text-neutral-200"
        >
          ×
        </button>
      ) : (
        <div className="flex justify-end gap-1.5">
          <button type="button" onClick={() => onReview("rejected")} className="btn btn-ghost btn-sm">
            Reject
          </button>
          <button type="button" onClick={() => onReview("approved")} className="btn btn-primary btn-sm">
            Approve
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * The Points page's card: every undismissed change, net since last dismissed.
 * Held readings are left to the page's own Needs review, which lists every
 * hold whatever its source, so one is not shown twice.
 */
export function WhatChanged({
  changes,
  today,
  onError,
}: {
  changes: PointsChange[];
  today: string;
  onError: (message: string) => void;
}) {
  return (
    <ChangesCard
      title="What changed"
      groups={[{ rows: changes.filter((c) => c.kind !== "held").map((c) => pointsRow(c, "net", today)) }]}
      onError={onError}
    />
  );
}

/** The Offers page's card: new offers worth adding, until dismissed. */
export function OfferChanges({
  changes,
  today,
  onError,
}: {
  changes: OfferChange[];
  today: string;
  onError: (message: string) => void;
}) {
  return (
    <ChangesCard
      title="What changed"
      groups={[{ rows: changes.map((c) => offerRow(c, today)) }]}
      onError={onError}
    />
  );
}
